import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, PlusIcon, TrashIcon, MoveUpIcon, MoveDownIcon, GripIcon } from 'lucide-react';
import { Catalog, CatalogSchema, CatalogField, DataType } from '../CatalogsManagement';
interface SchemaBuilderProps {
  schema: CatalogSchema | null;
  catalog: Catalog;
  onSave: (schemaData: CatalogSchema) => void;
  onCancel: () => void;
  locale: string;
}
export function SchemaBuilder({
  schema,
  catalog,
  onSave,
  onCancel,
  locale
}: SchemaBuilderProps) {
  // Schema state
  const [schemaData, setSchemaData] = useState<CatalogSchema>({
    id: schema?.id || '',
    name: schema?.name || `${catalog.name} Schema`,
    catalogId: catalog.id,
    fields: schema?.fields || []
  });
  // Handle field changes
  const handleFieldChange = (index: number, field: Partial<CatalogField>) => {
    const updatedFields = [...schemaData.fields];
    updatedFields[index] = {
      ...updatedFields[index],
      ...field
    };
    // If name changes, update key if key is empty or was auto-generated
    if (field.name && (!updatedFields[index].key || updatedFields[index].key === generateKeyFromName(updatedFields[index].name))) {
      updatedFields[index].key = generateKeyFromName(field.name as string);
    }
    setSchemaData({
      ...schemaData,
      fields: updatedFields
    });
  };
  // Generate a key from a field name
  const generateKeyFromName = (name: string): string => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, ' ').trim().replace(/\s+/g, '_');
  };
  // Add a new field
  const handleAddField = () => {
    const newField: CatalogField = {
      id: `field-${Date.now()}`,
      name: '',
      key: '',
      dataType: 'string',
      isRequired: false,
      order: schemaData.fields.length
    };
    setSchemaData({
      ...schemaData,
      fields: [...schemaData.fields, newField]
    });
  };
  // Remove a field
  const handleRemoveField = (index: number) => {
    const updatedFields = [...schemaData.fields];
    updatedFields.splice(index, 1);
    // Update order of remaining fields
    updatedFields.forEach((field, idx) => {
      field.order = idx;
    });
    setSchemaData({
      ...schemaData,
      fields: updatedFields
    });
  };
  // Move a field up
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updatedFields = [...schemaData.fields];
    const temp = updatedFields[index];
    updatedFields[index] = updatedFields[index - 1];
    updatedFields[index - 1] = temp;
    // Update order
    updatedFields.forEach((field, idx) => {
      field.order = idx;
    });
    setSchemaData({
      ...schemaData,
      fields: updatedFields
    });
  };
  // Move a field down
  const handleMoveDown = (index: number) => {
    if (index === schemaData.fields.length - 1) return;
    const updatedFields = [...schemaData.fields];
    const temp = updatedFields[index];
    updatedFields[index] = updatedFields[index + 1];
    updatedFields[index + 1] = temp;
    // Update order
    updatedFields.forEach((field, idx) => {
      field.order = idx;
    });
    setSchemaData({
      ...schemaData,
      fields: updatedFields
    });
  };
  // Handle schema name change
  const handleSchemaNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSchemaData({
      ...schemaData,
      name: e.target.value
    });
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(schemaData);
  };
  // Get data type options
  const dataTypeOptions: {
    value: DataType;
    label: string;
  }[] = [{
    value: 'string',
    label: 'Text'
  }, {
    value: 'number',
    label: 'Number'
  }, {
    value: 'boolean',
    label: 'Yes/No'
  }, {
    value: 'date',
    label: 'Date'
  }, {
    value: 'dropdown',
    label: 'Dropdown'
  }, {
    value: 'currency',
    label: 'Currency'
  }, {
    value: 'url',
    label: 'URL'
  }];
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-13776">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-13777">
        <div data-id="element-13778">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-13779">
            Schema Builder
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-13780">
            Define custom fields for products in this catalog
          </p>
        </div>
        <button onClick={onCancel} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13781">
          <XIcon className="h-5 w-5" data-id="element-13782" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-13783">
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6" data-id="element-13784">
          {/* Schema Name */}
          <div className="mb-6" data-id="element-13785">
            <label htmlFor="schemaName" className="block text-sm font-medium text-gray-700" data-id="element-13786">
              Schema Name
            </label>
            <input type="text" name="schemaName" id="schemaName" className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={schemaData.name} onChange={handleSchemaNameChange} required data-id="element-13787" />
          </div>
          {/* Fields Table */}
          <div className="mt-6" data-id="element-13788">
            <div className="flex justify-between items-center mb-4" data-id="element-13789">
              <h4 className="text-base font-medium text-gray-900" data-id="element-13790">
                Custom Fields
              </h4>
              <button type="button" onClick={handleAddField} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13791">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-13792" />
                Add Field
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-13793">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-13794">
                <thead className="bg-gray-50" data-id="element-13795">
                  <tr data-id="element-13796">
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-13797">
                      <span className="sr-only" data-id="element-13798">Order</span>
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13799">
                      Field Name
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13800">
                      Key
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13801">
                      Data Type
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13802">
                      Required
                    </th>
                    <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13803">
                      Options
                    </th>
                    <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24" data-id="element-13804">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-13805">
                  {schemaData.fields.length === 0 ? <tr data-id="element-13806">
                      <td colSpan={7} className="px-3 py-4 text-center text-sm text-gray-500" data-id="element-13807">
                        No fields defined. Click "Add Field" to create your
                        first custom field.
                      </td>
                    </tr> : schemaData.fields.map((field, index) => <tr key={field.id} className="hover:bg-gray-50" data-id="element-13808">
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13809">
                          <GripIcon className="h-4 w-4 text-gray-400" data-id="element-13810" />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap" data-id="element-13811">
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={field.name} onChange={e => handleFieldChange(index, {
                      name: e.target.value
                    })} placeholder="Field Name" required data-id="element-13812" />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap" data-id="element-13813">
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={field.key} onChange={e => handleFieldChange(index, {
                      key: e.target.value
                    })} placeholder="auto_generated" required data-id="element-13814" />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap" data-id="element-13815">
                          <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={field.dataType} onChange={e => handleFieldChange(index, {
                      dataType: e.target.value as DataType
                    })} data-id="element-13816">
                            {dataTypeOptions.map(option => <option key={option.value} value={option.value} data-id="element-13817">
                                {option.label}
                              </option>)}
                          </select>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap" data-id="element-13818">
                          <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={field.isRequired} onChange={e => handleFieldChange(index, {
                      isRequired: e.target.checked
                    })} data-id="element-13819" />
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap" data-id="element-13820">
                          {field.dataType === 'dropdown' && <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={field.options?.join(', ') || ''} onChange={e => handleFieldChange(index, {
                      options: e.target.value.split(',').map(opt => opt.trim()).filter(opt => opt)
                    })} placeholder="Option1, Option2, Option3" data-id="element-13821" />}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-13822">
                          <div className="flex justify-end space-x-2" data-id="element-13823">
                            <button type="button" onClick={() => handleMoveUp(index)} disabled={index === 0} className={`text-gray-400 hover:text-gray-500 ${index === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-13824">
                              <MoveUpIcon className="h-4 w-4" data-id="element-13825" />
                            </button>
                            <button type="button" onClick={() => handleMoveDown(index)} disabled={index === schemaData.fields.length - 1} className={`text-gray-400 hover:text-gray-500 ${index === schemaData.fields.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-13826">
                              <MoveDownIcon className="h-4 w-4" data-id="element-13827" />
                            </button>
                            <button type="button" onClick={() => handleRemoveField(index)} className="text-red-600 hover:text-red-900" data-id="element-13828">
                              <TrashIcon className="h-4 w-4" data-id="element-13829" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
            {schemaData.fields.length === 0 && <div className="mt-4 flex justify-center" data-id="element-13830">
                <button type="button" onClick={handleAddField} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13831">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-13832" />
                  Add Your First Field
                </button>
              </div>}
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-13833">
          <button type="button" className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={onCancel} data-id="element-13834">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13835">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-13836" />
            Save Schema
          </button>
        </div>
      </form>
    </div>;
}